import { notifyError } from "@/lib/notify-error";
import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Archive, Copy, Edit, GripVertical, Info, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { IconButton } from "@/components/ui/icon-button";
import { InfoHint } from "@/components/ui/info-hint";
import { fromDateInputValue, toDateInputValue } from "@/lib/date-input";
import { EditAccessGate } from "@/features/onboarding";

export const Route = createFileRoute("/specPilot/templates")({
  component: SubmittalTemplatesPage,
});

type CsiField =
  | "csi_code"
  | "csi_title"
  | "package_spec_section_number"
  | "package_spec_section_description"
  | "submittal_number";

type TemplateColumn =
  | { key: string; header: string; type: "title" }
  | { key: string; header: string; type: "normal"; defaultValue?: string }
  | { key: string; header: string; type: "generated"; description: string }
  | { key: string; header: string; type: "description" }
  | { key: string; header: string; type: "source_column"; sourceHeader: string; fallbackValue?: string }
  | { key: string; header: string; type: "csi"; csiField: CsiField }
  | { key: string; header: string; type: "team_member"; defaultValue?: string }
  | { key: string; header: string; type: "project_item"; defaultValue?: string }
  | { key: string; header: string; type: "duration"; defaultValue?: string }
  | { key: string; header: string; type: "date"; defaultValue?: string };

type TemplateOption = {
  id: string;
  source: "builtin" | "custom";
  name: string;
  description: string;
  version: number;
  columns: TemplateColumn[];
  archived?: boolean;
};

type DraftTemplate = {
  id?: Id<"specPilotSubmittalTemplates">;
  name: string;
  description: string;
  columns: TemplateColumn[];
};

const CSI_FIELD_LABELS: Record<CsiField, string> = {
  csi_code: "CSI Code",
  csi_title: "CSI Title",
  package_spec_section_number: "Package Spec Section Number",
  package_spec_section_description: "Package Spec Section Description",
  submittal_number: "Submittal Number",
};

const DEFAULT_HEADERS_BY_TYPE: Partial<Record<TemplateColumn["type"], string>> = {
  normal: "New Column",
  team_member: "Team Member",
  project_item: "Project Item",
  duration: "Duration",
  date: "Date",
};

function columnKeyFromHeader(header: string) {
  return header
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "column";
}

function createColumn(type: TemplateColumn["type"] = "normal"): TemplateColumn {
  if (type === "title") {
    const header = "Title";
    return { key: columnKeyFromHeader(header), header, type };
  }
  if (type === "generated") {
    const header = "Generated Column";
    return { key: columnKeyFromHeader(header), header, type, description: "Generate this value from the extracted row context." };
  }
  if (type === "description") {
    const header = "Description";
    return { key: columnKeyFromHeader(header), header, type };
  }
  if (type === "source_column") {
    const header = "Schedule Column";
    return { key: columnKeyFromHeader(header), header, type, sourceHeader: header, fallbackValue: "" };
  }
  if (type === "csi") {
    const header = "CSI Code";
    return { key: columnKeyFromHeader(header), header, type, csiField: "csi_code" };
  }
  const header = DEFAULT_HEADERS_BY_TYPE[type] ?? "New Column";
  return { key: columnKeyFromHeader(header), header, type, defaultValue: "" };
}

function validateDraft(draft: DraftTemplate) {
  if (!draft.name.trim()) return "Template name is required";
  if (draft.columns.length === 0) return "Add at least one column";
  if (draft.columns[0]?.type !== "title") return "The first column must be the title column";
  if (draft.columns.filter((column) => column.type === "title").length !== 1) {
    return "Templates must have exactly one title column";
  }
  const headers = new Set<string>();
  const keys = new Set<string>();
  for (const column of draft.columns) {
    if (!column.header.trim()) return "Every column needs a header";
    const headerKey = column.header.trim().toLowerCase();
    if (headers.has(headerKey)) return `Duplicate column header: ${column.header}`;
    headers.add(headerKey);
    const key = columnKeyFromHeader(column.header);
    if (keys.has(key)) return `Duplicate normalized column name: ${column.header}`;
    keys.add(key);
    if (column.type === "generated" && !column.description.trim()) {
      return `Generated column "${column.header}" needs a description`;
    }
    if (column.type === "source_column" && !column.sourceHeader.trim()) {
      return `Schedule column "${column.header}" needs a matching column name`;
    }
  }
  return null;
}

function serializeDraft(draft: DraftTemplate) {
  return JSON.stringify({
    id: draft.id,
    name: draft.name,
    description: draft.description,
    columns: draft.columns,
  });
}

export function SubmittalTemplatesPage() {
  return (
    <EditAccessGate>
      <SubmittalTemplatesPageContent />
    </EditAccessGate>
  );
}

function SubmittalTemplatesPageContent() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data } = useQuery(convexQuery(api.templates.list, { includeArchived }));
  const templates = (data ?? []) as TemplateOption[];
  const customTemplates = templates.filter((template) => template.source === "custom");
  const builtInTemplates = templates.filter((template) => template.source === "builtin");
  const saveTemplate = useMutation(api.templates.save);
  const archiveTemplate = useMutation(api.templates.archive);
  const duplicateTemplate = useMutation(api.templates.duplicate);
  const [draft, setDraft] = useState<DraftTemplate | null>(null);
  const [savedDraftSnapshot, setSavedDraftSnapshot] = useState<string | null>(null);
  const [saveMode, setSaveMode] = useState<"overwrite" | "new_version">("overwrite");

  const validationMessage = useMemo(() => (draft ? validateDraft(draft) : null), [draft]);
  const hasChanges = draft !== null && serializeDraft(draft) !== savedDraftSnapshot;

  function startNewTemplate() {
    setSaveMode("overwrite");
    setSavedDraftSnapshot(null);
    const nextDraft: DraftTemplate = {
      name: "New Submittal Template",
      description: "",
      columns: [createColumn("title"), createColumn("description"), createColumn("source_column"), createColumn("csi"), createColumn("normal")],
    };
    setDraft(nextDraft);
  }

  function editTemplate(template: TemplateOption) {
    if (template.source !== "custom") return;
    setSaveMode("overwrite");
    const nextDraft: DraftTemplate = {
      id: template.id as Id<"specPilotSubmittalTemplates">,
      name: template.name,
      description: template.description,
      columns: template.columns,
    };
    setDraft(nextDraft);
    setSavedDraftSnapshot(serializeDraft(nextDraft));
  }

  async function handleDuplicate(template: TemplateOption) {
    try {
      const result = await duplicateTemplate({ id: template.id });
      const duplicated = result as TemplateOption;
      toast.success(`Duplicated ${template.name}`);
      const nextDraft: DraftTemplate = {
        id: duplicated.id as Id<"specPilotSubmittalTemplates">,
        name: duplicated.name,
        description: duplicated.description,
        columns: duplicated.columns,
      };
      setDraft(nextDraft);
      setSavedDraftSnapshot(serializeDraft(nextDraft));
    } catch (error) {
      console.error("Failed to duplicate submittal template", error);
      notifyError(error, "Failed to duplicate template");
    }
  }

  async function handleSave() {
    if (!draft) return;
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }
    try {
      const result = await saveTemplate({
        id: draft.id,
        name: draft.name,
        description: draft.description,
        columns: draft.columns,
        mode: draft.id ? saveMode : undefined,
      });
      const saved = result as TemplateOption;
      const nextDraft: DraftTemplate = {
        id: saved.id as Id<"specPilotSubmittalTemplates">,
        name: saved.name,
        description: saved.description,
        columns: saved.columns,
      };
      setDraft(nextDraft);
      setSavedDraftSnapshot(serializeDraft(nextDraft));
      setSaveMode("overwrite");
      toast.success(`Template saved: ${saved.name} v${saved.version}`);
    } catch (error) {
      console.error("Failed to save submittal template", error);
      notifyError(error, "Failed to save template");
    }
  }

  async function handleArchive(template: TemplateOption, archived: boolean) {
    if (template.source !== "custom") return;
    try {
      await archiveTemplate({
        id: template.id as Id<"specPilotSubmittalTemplates">,
        archived,
      });
      if (archived && draft?.id === template.id) {
        setDraft(null);
        setSavedDraftSnapshot(null);
        setSaveMode("overwrite");
      }
      toast.success(archived ? "Template archived" : "Template restored");
    } catch (error) {
      console.error("Failed to update submittal template archive state", error);
      notifyError(error, archived ? "Failed to archive template" : "Failed to restore template");
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Submittal Templates"
        trail={[{ label: "SpecPilot", href: "/specPilot" }]}
        actions={
          <Button size="sm" onClick={startNewTemplate}>
            <Plus />
            New Template
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(280px,360px)_1fr]">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox id="include-archived" checked={includeArchived} onCheckedChange={(checked) => setIncludeArchived(checked === true)} />
            <Label htmlFor="include-archived">Show archived templates</Label>
          </div>

          <TemplateListSection
            title="Custom Templates"
            templates={customTemplates}
            emptyLabel="No custom templates yet."
            onEdit={editTemplate}
            onDuplicate={handleDuplicate}
            onArchive={handleArchive}
          />
          <TemplateListSection
            title="Built-in Starters"
            templates={builtInTemplates}
            emptyLabel="No built-in templates available."
            onEdit={editTemplate}
            onDuplicate={handleDuplicate}
            onArchive={handleArchive}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{draft ? "Template Builder" : "Select or create a template"}</CardTitle>
            <CardDescription>
              Configure manual defaults, generated-column descriptions, schedule columns, and CSI fields.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {draft ? (
              <TemplateEditor
                draft={draft}
                saveMode={saveMode}
                validationMessage={validationMessage}
                hasChanges={hasChanges}
                onDraftChange={setDraft}
                onSaveModeChange={setSaveMode}
                onSave={() => void handleSave()}
              />
            ) : (
              <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                Create a new template or edit an existing custom template.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function TemplateListSection({
  title,
  templates,
  emptyLabel,
  onEdit,
  onDuplicate,
  onArchive,
}: {
  title: string;
  templates: TemplateOption[];
  emptyLabel: string;
  onEdit: (template: TemplateOption) => void;
  onDuplicate: (template: TemplateOption) => Promise<void>;
  onArchive: (template: TemplateOption, archived: boolean) => Promise<void>;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {templates.length === 0 ? <p className="text-sm text-muted-foreground">{emptyLabel}</p> : null}
        {/* Nested list rows: thin border inside Card border-2 */}
        {templates.map((template) => (
          <div key={template.id} className="rounded-md border p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">
                  {template.name} {template.source === "custom" ? `v${template.version}` : ""}
                </p>
                <p className="line-clamp-2 text-xs text-muted-foreground">{template.description}</p>
                {template.archived ? <p className="mt-1 text-xs font-medium text-status-pending">Archived</p> : null}
              </div>
              <div className="flex shrink-0 gap-1">
                {template.source === "custom" ? (
                  <Button variant="outline" size="sm" onClick={() => onEdit(template)}>
                    <Edit />
                    Edit
                  </Button>
                ) : null}
                <IconButton
                  label="Duplicate template"
                  icon={Copy}
                  onClick={() => void onDuplicate(template)}
                />
                {template.source === "custom" ? (
                  <IconButton
                    label={template.archived ? "Restore template" : "Archive template"}
                    icon={template.archived ? RotateCcw : Archive}
                    onClick={() => void onArchive(template, !template.archived)}
                  />
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function TemplateEditor({
  draft,
  saveMode,
  validationMessage,
  hasChanges,
  onDraftChange,
  onSaveModeChange,
  onSave,
}: {
  draft: DraftTemplate;
  saveMode: "overwrite" | "new_version";
  validationMessage: string | null;
  hasChanges: boolean;
  onDraftChange: (draft: DraftTemplate) => void;
  onSaveModeChange: (mode: "overwrite" | "new_version") => void;
  onSave: () => void;
}) {
  const [columnOptionsOpen, setColumnOptionsOpen] = useState(false);

  useEffect(() => {
    if (!draft.id) onSaveModeChange("overwrite");
  }, [draft.id, onSaveModeChange]);

  function updateColumn(index: number, column: TemplateColumn) {
    if (index === 0 && column.type !== "title") return;
    onDraftChange({
      ...draft,
      columns: draft.columns.map((existing, i) => (i === index ? column : existing)),
    });
  }

  function removeColumn(index: number) {
    if (index === 0) return;
    onDraftChange({ ...draft, columns: draft.columns.filter((_, i) => i !== index) });
  }

  function addColumn(type: TemplateColumn["type"]) {
    onDraftChange({ ...draft, columns: [...draft.columns, createColumn(type)] });
  }

  function moveColumn(fromIndex: number, toIndex: number) {
    if (fromIndex === toIndex || fromIndex === 0 || toIndex === 0) return;
    const nextColumns = [...draft.columns];
    const [moved] = nextColumns.splice(fromIndex, 1);
    nextColumns.splice(toIndex, 0, moved);
    onDraftChange({ ...draft, columns: nextColumns });
  }

  const saveDisabledReason = validationMessage ?? (!hasChanges ? "No changes to save" : null);

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="template-name">Template Name</Label>
          <Input id="template-name" value={draft.name} onChange={(event) => onDraftChange({ ...draft, name: event.target.value })} />
        </div>
        {draft.id ? (
          <div className="space-y-2">
            <Label htmlFor="save-mode">Save Behavior</Label>
            <Select value={saveMode} onValueChange={(value) => onSaveModeChange(value as "overwrite" | "new_version")}>
              <SelectTrigger id="save-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="overwrite">Override existing version</SelectItem>
                <SelectItem value="new_version">Create new version</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="template-description">Description</Label>
        <Input id="template-description" value={draft.description} onChange={(event) => onDraftChange({ ...draft, description: event.target.value })} />
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium">Columns</h3>
          <IconButton
            label="Column option descriptions"
            icon={Info}
            onClick={() => setColumnOptionsOpen(true)}
          />
        </div>
        <AddColumnButton onAddColumn={addColumn} />
      </div>

      <ColumnOptionsDialog open={columnOptionsOpen} onOpenChange={setColumnOptionsOpen} />

      <div className="space-y-3">
        {draft.columns.map((column, index) => (
          <ColumnEditor
            key={`column-${index}`}
            column={column}
            index={index}
            onChange={(next) => updateColumn(index, next)}
            onRemove={() => removeColumn(index)}
            onMove={moveColumn}
          />
        ))}
        <div className="flex justify-center">
          <AddColumnButton onAddColumn={addColumn} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        {validationMessage ? <p className="text-sm font-medium text-destructive">{validationMessage}</p> : <span />}
        <span className="inline-block" title={saveDisabledReason ?? "Save template changes"}>
          <Button onClick={onSave} disabled={Boolean(saveDisabledReason)}>
            <Save />
            Save Template
          </Button>
        </span>
      </div>
    </div>
  );
}

function AddColumnButton({ onAddColumn }: { onAddColumn: (type: TemplateColumn["type"]) => void }) {
  return (
    <IconButton
      label="Add manual column"
      icon={Plus}
      variant="default"
      onClick={() => onAddColumn("normal")}
    />
  );
}

function ColumnOptionsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const options = [
    ["Title", "Required first column. Stores each submittal title, typical action title, and merged row group name."],
    ["Manual", "A column you fill with a default value. This is useful for consistent status, notes, or fields that are not pulled from the schedule."],
    ["Generated", "A column populated from schedule context based on the description you provide."],
    ["Description", "Uses SpecPilot's standard generated submittal description for each row."],
    ["Schedule Column", "Copies values from a named column in the extracted schedule, such as Comments, Remarks, or Manufacturer."],
    ["CSI", "Adds CSI classification fields such as CSI Code, CSI Title, package section number, or submittal number."],
    ["Team Member", "A manual default intended for responsible team member fields."],
    ["Project Item", "A manual default intended for project-specific tracking fields."],
    ["Duration", "A numeric manual default for review durations or lead-time style fields."],
    ["Date", "A date manual default for template fields that should use a calendar value."],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Template column options</DialogTitle>
          <DialogDescription>Choose how each output column should be populated when SpecPilot generates a submittal.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {options.map(([title, description]) => (
            <div key={title} className="rounded-md border p-3">
              <p className="text-sm font-medium">{title}</p>
              <p className="mt-1 text-xs text-muted-foreground">{description}</p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ColumnEditor({
  column,
  index,
  onChange,
  onRemove,
  onMove,
}: {
  column: TemplateColumn;
  index: number;
  onChange: (column: TemplateColumn) => void;
  onRemove: () => void;
  onMove: (fromIndex: number, toIndex: number) => void;
}) {
  const isLockedTitleColumn = index === 0 && column.type === "title";

  function changeType(type: TemplateColumn["type"]) {
    const base = { key: columnKeyFromHeader(column.header), header: column.header };
    if (type === "title") onChange({ ...base, type });
    if (type === "generated") onChange({ ...base, type, description: "Generate this value from the extracted row context." });
    if (type === "description") {
      const header = "Description";
      onChange({ key: columnKeyFromHeader(header), header, type });
    }
    if (type === "source_column") onChange({ ...base, type, sourceHeader: column.header, fallbackValue: "" });
    if (type === "csi") onChange({ ...base, type, csiField: "csi_code" });
    if (["normal", "team_member", "project_item", "duration", "date"].includes(type)) {
      onChange({ ...base, type, defaultValue: "" } as TemplateColumn);
    }
  }

  return (
    <div
      className="grid gap-3 rounded-md border p-3 lg:grid-cols-[auto_1fr_180px_1.4fr_auto]"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        const fromIndex = Number(event.dataTransfer.getData("text/plain"));
        if (Number.isInteger(fromIndex)) onMove(fromIndex, index);
      }}
    >
      <button
        type="button"
        className="flex items-center self-stretch text-muted-foreground disabled:cursor-not-allowed disabled:opacity-40 enabled:cursor-grab enabled:active:cursor-grabbing"
        title={isLockedTitleColumn ? "Title column is locked first" : "Drag to reorder"}
        aria-label={isLockedTitleColumn ? "Title column locked first" : "Drag to reorder column"}
        draggable={!isLockedTitleColumn}
        disabled={isLockedTitleColumn}
        onDragStart={(event) => event.dataTransfer.setData("text/plain", String(index))}
      >
        <GripVertical className="size-4" />
      </button>
      <div className="space-y-1">
        <Label htmlFor={`column-header-${column.key}`} className="text-xs">Column {index + 1} Name</Label>
        <Input
          id={`column-header-${column.key}`}
          value={column.header}
          onChange={(event) => {
            const header = event.target.value;
            onChange({ ...column, header, key: columnKeyFromHeader(header) });
          }}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Type</Label>
        <Select value={column.type} onValueChange={(value) => changeType(value as TemplateColumn["type"])} disabled={isLockedTitleColumn}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {isLockedTitleColumn ? <SelectItem value="title">Title</SelectItem> : null}
            <SelectItem value="normal">Manual</SelectItem>
            <SelectItem value="generated">Generated</SelectItem>
            <SelectItem value="description">Description</SelectItem>
            <SelectItem value="source_column">Schedule Column</SelectItem>
            <SelectItem value="csi">CSI</SelectItem>
            <SelectItem value="team_member">Team Member</SelectItem>
            <SelectItem value="project_item">Project Item</SelectItem>
            <SelectItem value="duration">Duration</SelectItem>
            <SelectItem value="date">Date</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <ColumnTypeConfig column={column} onChange={onChange} />
      <IconButton
        label="Remove column"
        icon={Trash2}
        size="md"
        onClick={onRemove}
        className="self-end"
        disabled={isLockedTitleColumn}
      />
    </div>
  );
}

function HelpTooltip({ children }: { children: string }) {
  return <InfoHint label="Column type help">{children}</InfoHint>;
}

function ColumnTypeConfig({ column, onChange }: { column: TemplateColumn; onChange: (column: TemplateColumn) => void }) {
  if (column.type === "title") {
    return (
      <div className="space-y-1 rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
        Required first column. SpecPilot writes submittal titles, typical actions, and merged row group names here.
      </div>
    );
  }

  if (column.type === "generated") {
    return (
      <div className="space-y-1">
        <Label htmlFor={`column-description-${column.key}`} className="flex items-center gap-1 text-xs">
          Generated Description
          <HelpTooltip>Describe the information generated from schedule</HelpTooltip>
        </Label>
        <Input
          id={`column-description-${column.key}`}
          value={column.description}
          onChange={(event) => onChange({ ...column, description: event.target.value })}
          placeholder="Tell the AI what to generate"
        />
      </div>
    );
  }

  if (column.type === "csi") {
    return (
      <div className="space-y-1">
        <Label className="flex items-center gap-1 text-xs">CSI Field <HelpTooltip>Populate this column with an AI-classified CSI value for the submittal row.</HelpTooltip></Label>
        <Select value={column.csiField} onValueChange={(value) => onChange({ ...column, csiField: value as CsiField })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(CSI_FIELD_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (column.type === "description") {
    return (
      <div className="space-y-1 rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
        Uses the existing SpecPilot description generation for each submittal row.
      </div>
    );
  }

  if (column.type === "source_column") {
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={`source-header-${column.key}`} className="flex items-center gap-1 text-xs">Column Name <HelpTooltip>Copy data from a matching extracted schedule column. You can override this during generation.</HelpTooltip></Label>
          <Input
            id={`source-header-${column.key}`}
            value={column.sourceHeader}
            onChange={(event) => onChange({ ...column, sourceHeader: event.target.value })}
            placeholder="Exact schedule column name"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`source-fallback-${column.key}`} className="text-xs">Default</Label>
          <Input
            id={`source-fallback-${column.key}`}
            value={column.fallbackValue ?? ""}
            onChange={(event) => onChange({ ...column, fallbackValue: event.target.value })}
            placeholder="Optional fallback"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <Label htmlFor={`column-default-${column.key}`} className="flex items-center gap-1 text-xs">Default Value <HelpTooltip>Manual columns use this value unless you override it when generating a submittal.</HelpTooltip></Label>
      <Input
        id={`column-default-${column.key}`}
        type={column.type === "date" ? "date" : "text"}
        value={column.type === "date" ? toDateInputValue(column.defaultValue) : column.defaultValue ?? ""}
        onChange={(event) => onChange({
          ...column,
          defaultValue: column.type === "date"
            ? fromDateInputValue(event.target.value)
            : event.target.value,
        })}
        placeholder="Optional default"
      />
    </div>
  );
}
